import { add, qs, qsa, remove, listen } from '@fluorescent/dom';
import Delegate from 'ftdomdelegate';
import { Ajaxinate } from 'ajaxinate';

import { section } from '@/glow';
import { emit } from '@/glow/events';
import sidebar from '@/lib/collection/sidebar';
import { AjaxinateShim } from '@/lib/shame';
import ProductTile from '@/lib/product-tile';
import filterHandler from '@/lib/filters/filter-handler';
import filterFlyout from '@/lib/filters/filter-flyout';
import { updateFilters } from '@/lib/filters/events';

const selectors = {
  infiniteScrollContainer: '.collection__infinite-container',
  infiniteScrollTrigger: '.collection__infinite-trigger',
  sidebar: '[data-sidebar]',
  partial: '[data-partial]',
  perPageContainer: '[data-collection-templates]',
  productTile: '[data-product-tile]',
};

section('collection', {
  onLoad() {
    const { paginationType } = this.container.dataset;
    this.paginationType = paginationType;

    this.sidebar = sidebar(qs(selectors.sidebar, this.container));

    this.filterForm = qs('[data-filter-form]', this.container);

    if (this.filterForm) {
      this.partial = qs(selectors.partial, this.container);

      this.filterFlyout = filterFlyout(this.container);
      this.filterHandler = filterHandler({
        container: this.container,
        partial: this.partial,
        renderCB: this._renderView.bind(this),
      });
      this.stateListener = listen(window, 'popstate', event => {
        const searchParams = event.state
          ? event.state.searchParams
          : this.searchParamsInitial;
        if (searchParams === this.searchParamsPrev) return;
        this._renderView(searchParams, false);
      });
    }

    this.productTiles = qsa(selectors.productTile, this.container).map(tile =>
      ProductTile(tile)
    );

    this._initDelegate();
    this._initInfiniteScroll();
  },

  _initDelegate() {
    this.delegate = new Delegate(this.container);

    this.delegate.on('click', '[data-per-page]', e => {
      e.preventDefault();
      const { collectionTemplate } = e.target.dataset;
      const viewInput = qs('[data-per-page-input]', this.filterForm);
      const allViewLinks = qsa('[data-per-page]', this.container);

      allViewLinks.forEach(link => {
        remove(link, 'active');
      });

      add(e.target, 'active');

      viewInput.value = collectionTemplate;
      updateFilters(this.filterForm);
    });
  },

  _renderView(searchParams, updateHash = true) {
    const url = `${window.location.pathname}?section_id=${this.container.dataset.sectionId}&${searchParams}`;
    const loading = qs('.collection__loading', this.container);

    add(loading, 'is-active');
    add(this.container, 'is-filtered');

    fetch(url, { credentials: 'include' })
      .then(res => res.text())
      .then(res => {
        updateHash && this._updateURLHash(searchParams);
        const doc = new window.DOMParser().parseFromString(res, 'text/html');
        const contents = qs(selectors.partial, doc).innerHTML;
        this.partial.innerHTML = contents;

        this._initInfiniteScroll();

        remove(loading, 'is-active');
        emit('collection:updated');

        this.productTiles = qsa(selectors.productTile, this.container).map(
          tile => ProductTile(tile)
        );
      });
  },

  _updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  },

  _onHistoryChange(event) {},

  _initInfiniteScroll() {
    const paginated = this.paginationType === 'paginated';
    const infiniteScrollTrigger = qs(
      selectors.infiniteScrollTrigger,
      this.container
    );

    if (paginated || !infiniteScrollTrigger) return;

    const infiniteScrollOptions = {
      container: selectors.infiniteScrollContainer,
      pagination: selectors.infiniteScrollTrigger,
      loadingText: 'Loading...',
      callback: () =>
        (this.productTiles = qsa(selectors.productTile, this.container).map(
          tile => ProductTile(tile)
        )),
    };

    if (this.paginationType === 'click') {
      infiniteScrollOptions.method = 'click';
    }

    AjaxinateShim(Ajaxinate);

    this.infiniteScroll = new Ajaxinate(infiniteScrollOptions);
  },

  onUnload() {
    this.sidebar();

    this.delegate.off();
    this.subscriptions.forEach(unsubscribe => unsubscribe());
    this.productTiles.forEach(tile => tile.unload());
    this.stateListener && this.stateListener();
    this.filterFlyout && this.filterFlyout.unload();
  },
});
